import { API_BASE_URL, getBaseHeaders } from './api.config';
import { ApiError, BackendError, parseApiErrorResponse, throwApiError } from './api.error';
import {
  CompanyLookupResult,
  UpdateUserAccountTypeRequest,
  User,
  UserProfile,
} from './api.types';
import { authenticatedCall, optionallyAuthenticatedCall } from './auth';

export interface UpdateUserProfileRequest {
  firstName: string;
  lastName: string;
  companyName?: string;
  phone?: string;
  description?: string;
  termsAndConditions: boolean;
  marketingEmails: boolean;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
}

export const fetchUserProfile = async (): Promise<UserProfile> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/me`,
    {
      method: 'GET',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const updateUserAccountType = async (data: UpdateUserAccountTypeRequest): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/me/account-type`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const lookupCompanyByTaxId = async (
  taxIdentificationNumber: string
): Promise<CompanyLookupResult> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/me/company-lookup`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ taxIdentificationNumber })
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const updateUserProfile = async (
  data: UpdateUserProfileRequest
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok && response.status !== 204) {
    const errorData = await parseApiErrorResponse(response);
    const errors: BackendError[] = Array.isArray(errorData.errors) ? errorData.errors : [];

    if (errors.some((error) => error.code === 'user_invalid_phone_number')) {
      throw new Error('INVALID_PHONE_NUMBER');
    }
    if (errors.some((error) => error.code === 'user_invalid_facebook_url')) {
      throw new Error('INVALID_FACEBOOK_URL');
    }
    if (errors.some((error) => error.code === 'user_invalid_instagram_url')) {
      throw new Error('INVALID_INSTAGRAM_URL');
    }
    if (errors.some((error) => error.code === 'user_invalid_tiktok_url')) {
      throw new Error('INVALID_TIKTOK_URL');
    }

    throw new ApiError(response.status, errorData);
  }
};

export const updateUserPicture = async (
  imageUrl: string
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/picture`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ imageUrl })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const deleteUserPicture = async (): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/picture`,
    {
      method: 'DELETE',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const fetchUserById = async (userId: string, accessToken?: string | null): Promise<User> => {
  const url = `${API_BASE_URL}/users/${userId}/view`;
  const options: RequestInit = {
    method: 'GET',
    headers: getBaseHeaders()
  };
  const response = accessToken
    ? await authenticatedCall(url, options)
    : await optionallyAuthenticatedCall(url, options);

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const rateUser = async (
  userId: string,
  rate: number
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/${userId}/rate`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ rate })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const reportUser = async (
  userId: string,
  reasons: string[]
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/${userId}/report`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reasons })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const deleteAccount = async (): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/me`,
    {
      method: 'DELETE',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export interface NotificationSetting {
  notificationType: 'PRODUCT_ACTIVATED' | 'PRODUCT_DECLINED' | 'PRODUCT_EXPIRED' | 'MARKETING' | 'CHAT_MESSAGES';
  status: boolean;
}

export const fetchNotificationSettings = async (): Promise<NotificationSetting[]> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/settings`,
    {
      method: 'GET',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const updateNotificationSetting = async (
  notificationType: NotificationSetting['notificationType'],
  status: boolean
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/users/settings`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ notificationType, status })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};
